/**
 * Regras de TELA da execução técnica — rótulos, classificação ao vivo e variação.
 * São as mesmas contas de `work-order-inspection-rules.ts` da API (que valida de novo
 * ao gravar); aqui só servem para dar retorno enquanto a pessoa digita.
 */
import type {
  MaintenanceExecFieldDto, MaintenanceExecutionConfigDto, MaintenanceInspectionConditionDto, MaintenanceInspectionDeltaDto,
  MaintenanceInterventionDto, MaintenancePostConditionDto,
} from "@bi/types";

export const COND_LABEL: Record<MaintenanceInspectionConditionDto, string> = {
  not_inspected: "Não inspecionado", ok: "Normal", attention: "Atenção", critical: "Crítico",
};

/** Cor nunca é o único sinal: cada condição tem rótulo e símbolo. */
export const COND_STYLE: Record<MaintenanceInspectionConditionDto, { badge: string; dot: string; symbol: string; ring: string }> = {
  not_inspected: { badge: "badge-gray", dot: "bg-gray-300", symbol: "·", ring: "border-gray-300" },
  ok: { badge: "badge-green", dot: "bg-tm-500", symbol: "✓", ring: "border-tm-500" },
  attention: { badge: "badge-amber", dot: "bg-warning-500", symbol: "⚠", ring: "border-warning-500" },
  critical: { badge: "badge-red", dot: "bg-danger-600", symbol: "✕", ring: "border-danger-600" },
};

export const INTERVENTION_LABEL: Record<MaintenanceInterventionDto, string> = {
  no_action: "Nenhuma", adjusted: "Regulagem", repaired: "Reparo", replaced: "Substituição", lubricated: "Lubrificação",
  cleaned: "Limpeza", tightened: "Aperto", pending: "Pendente", not_applicable: "Não se aplica", other: "Outra",
};

export const ALL_INTERVENTIONS = Object.keys(INTERVENTION_LABEL) as MaintenanceInterventionDto[];

export const POST_CONDITION: Record<MaintenancePostConditionDto, { label: string; badge: string; hint: string }> = {
  fit: { label: "Apto", badge: "badge-green", hint: "Nenhum problema em aberto." },
  fit_with_caveats: { label: "Apto com ressalvas", badge: "badge-amber", hint: "Há pendência ou componente em atenção." },
  requires_intervention: { label: "Requer intervenção", badge: "badge-red", hint: "Há componente crítico sem resolução." },
};

export const PROBLEM_STATE: Record<"resolved" | "pending" | "open", { label: string; badge: string }> = {
  resolved: { label: "Resolvido nesta O.S.", badge: "badge-green" },
  pending: { label: "Pendente", badge: "badge-amber" },
  open: { label: "Sem destino", badge: "badge-red" },
};

const RANK: Record<MaintenanceInspectionConditionDto, number> = { not_inspected: 0, ok: 1, attention: 2, critical: 3 };

export function classifyField(f: MaintenanceExecFieldDto, value: unknown): "ok" | "attention" | "critical" | null {
  if (f.type !== "number" && f.type !== "measurement") return null;
  if (f.warningThreshold == null && f.criticalThreshold == null) return null;
  if (value === null || value === undefined || value === "") return null;
  const v = Number(value);
  if (!Number.isFinite(v)) return null;
  const sobe = f.direction === "lower_is_better";
  const alem = (lim: number) => (sobe ? v > lim : v < lim);
  if (f.criticalThreshold != null && alem(f.criticalThreshold)) return "critical";
  if (f.warningThreshold != null && alem(f.warningThreshold)) return "attention";
  return "ok";
}

export function autoCondition(cfg: MaintenanceExecutionConfigDto, measures: Record<string, unknown>): MaintenanceInspectionConditionDto | null {
  let r: MaintenanceInspectionConditionDto | null = null;
  for (const f of cfg.fields) {
    const c = classifyField(f, measures[f.name]);
    if (c && (r === null || RANK[c] > RANK[r])) r = c;
  }
  return r;
}

/** Variação entre a medida anterior e a atual — só do mesmo campo numérico. */
export function deltasOf(
  cfg: MaintenanceExecutionConfigDto,
  prev: { measures: Record<string, unknown> | null; odometerKm: number | null },
  cur: { measures: Record<string, unknown>; odometerKm: number | null },
): MaintenanceInspectionDeltaDto[] {
  const out: MaintenanceInspectionDeltaDto[] = [];
  for (const f of cfg.fields) {
    if (f.type !== "number" && f.type !== "measurement") continue;
    const a = prev.measures?.[f.name], b = cur.measures[f.name];
    if (a === undefined || a === null || a === "" || b === undefined || b === null || b === "") continue;
    const from = Number(a), to = Number(b);
    if (!Number.isFinite(from) || !Number.isFinite(to)) continue;
    out.push({
      field: f.name, label: f.label, unit: f.unit ?? null, from, to, diff: Math.round((to - from) * 1000) / 1000,
      km: prev.odometerKm != null && cur.odometerKm != null && cur.odometerKm >= prev.odometerKm ? cur.odometerKm - prev.odometerKm : null,
    });
  }
  return out;
}

export const fmtVal = (v: unknown, unit?: string | null) =>
  v === null || v === undefined || v === "" ? "—" : `${Array.isArray(v) ? v.join(", ") : String(v).replace(".", ",")}${unit ? ` ${unit}` : ""}`;

export const fmtDiff = (d: number) => `${d > 0 ? "+" : ""}${String(d).replace(".", ",")}`;
